import type { AppKind } from "common/components/app-kind";
import { showConfirmModal, ConfirmModalStyle } from "common/components/confirm-modal";
import { getLocalizedString, formatLocalizedString } from "common/components/localization";
import type {
    UploadProgressHandler,
    UploadSession,
    UploadFileResponse,
    AppFacilities} from "common/components/types";
import {
    isUploadFileResponseError
} from "common/components/types";
import { FileSizeLimitExceededError, uploadFileIntoGlideStorage } from "common/components/upload-handlers";
import type { UploadComponentKind } from "common/firebase-function-types";

function getConfirmModalStringsForFailedUpload(
    component: UploadComponentKind,
    appKind: AppKind
): { title: string; description: string } {
    if (component === "signature") {
        return {
            title: getLocalizedString("cantAddNewSignatures", appKind),
            description: getLocalizedString("thisAppMustBeUpgradedToAllowMoreSignatures", appKind),
        };
    } else {
        return {
            title: getLocalizedString("thisActionCouldntBeTriggered", appKind),
            description: getLocalizedString("thisAppMustBeUpgraded", appKind),
        };
    }
}

export function uploadAppFile(
    appID: string,
    appKind: AppKind,
    appFacilities: AppFacilities,
    file: File,
    component: UploadComponentKind,
    onProgress: UploadProgressHandler | undefined,
    onFeedback: () => void
): UploadSession {
    appFacilities.trackEvent("upload_file", { app_id: appID, content_type: file.type, content_length: file.size });

    const onAttemptResponse = async (resp: UploadFileResponse) => {
        if (!isUploadFileResponseError(resp)) {
            appFacilities.trackEvent("upload success", { app_id: appID, label: component });
            return;
        }
        let pushFeedback = false;

        if (resp.quotaExceeded) {
            appFacilities.trackEvent("upload over quota", { app_id: appID, label: component });
            pushFeedback = !(
                (await showConfirmModal({
                    ...getConfirmModalStringsForFailedUpload(component, appKind),
                    accept: getLocalizedString("ok", appKind),
                    cancel: getLocalizedString("support", appKind),
                    style: ConfirmModalStyle.QuotaWarning,
                })) ?? true
            );
        } else if (resp.error instanceof FileSizeLimitExceededError) {
            const { givenSize, maxSize } = resp.error;
            pushFeedback = !(
                (await showConfirmModal({
                    title: getLocalizedString("fileTooLargeTitle", appKind),
                    description: formatLocalizedString(
                        "fileTooLargeDescription",
                        [givenSize, maxSize].map(x => numeral(x).format("0.0b"))
                    ),
                    accept: getLocalizedString("ok", appKind),
                    cancel: getLocalizedString("support", appKind),
                    style: ConfirmModalStyle.QuotaWarning,
                })) ?? true
            );
        }

        if (pushFeedback) {
            onFeedback();
            return;
        }

        appFacilities.trackEvent("upload failed", { app_id: appID, label: component });
    };
    return uploadFileIntoGlideStorage(appFacilities, appID, file, component, onProgress, onAttemptResponse);
}
